/**
 * FilterPersistence - Service for managing filter preferences persistence
 * 
 * Features:
 * - Save/load filter preferences to/from localStorage
 * - Validation and migration of saved preferences
 * - Export/import functionality for preferences
 * - Graceful handling of storage limitations
 * - Schema versioning for future compatibility
 */

import { BOOKMAKERS } from '../../surebets/constants/surebetTypes';

// Storage configuration
const STORAGE_CONFIG = {
  key: 'arbiwin_filter_preferences',
  version: '1.0.0',
  maxRetries: 3,
  retryDelay: 100, // ms
};

// Default filter preferences
const DEFAULT_PREFERENCES = {
  version: STORAGE_CONFIG.version,
  selectedBookmakers: Object.keys(BOOKMAKERS), // All bookmakers selected by default
  lastUpdated: new Date().toISOString(),
  schemaVersion: 1,
};

// Valid bookmaker IDs for validation
const VALID_BOOKMAKER_IDS = new Set(Object.keys(BOOKMAKERS));

/**
 * FilterPersistence - Service class for filter preferences persistence
 */
class FilterPersistence {
  /**
   * Save filter preferences to localStorage
   * @param {Object} preferences - Filter preferences to save
   * @param {string[]} preferences.selectedBookmakers - Array of selected bookmaker IDs
   * @param {string} preferences.lastUpdated - ISO timestamp of last update
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async saveFilterPreferences(preferences) {
    try {
      console.log('💾 FilterPersistence: Saving preferences:', preferences);
      
      // Validate input
      const validationResult = this.validatePreferences(preferences);
      if (!validationResult.isValid) {
        console.error('❌ FilterPersistence: Invalid preferences:', validationResult.errors);
        return { success: false, error: validationResult.errors.join(', ') };
      }

      // Prepare data for storage
      const dataToSave = {
        ...preferences,
        version: STORAGE_CONFIG.version,
        schemaVersion: 1,
        lastUpdated: new Date().toISOString(),
      };

      // Attempt to save with retry logic
      let lastError = null;
      for (let attempt = 1; attempt <= STORAGE_CONFIG.maxRetries; attempt++) {
        try {
          localStorage.setItem(STORAGE_CONFIG.key, JSON.stringify(dataToSave));
          console.log('✅ FilterPersistence: Successfully saved preferences');
          return { success: true };
        } catch (error) {
          lastError = error;
          console.warn(`⚠️ FilterPersistence: Save attempt ${attempt} failed:`, error.message);
          
          if (attempt < STORAGE_CONFIG.maxRetries) {
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, STORAGE_CONFIG.retryDelay * attempt));
          }
        }
      }

      // All retries failed
      console.error('❌ FilterPersistence: All save attempts failed:', lastError);
      return { 
        success: false, 
        error: `Failed to save preferences after ${STORAGE_CONFIG.maxRetries} attempts: ${lastError.message}` 
      };

    } catch (error) {
      console.error('❌ FilterPersistence: Unexpected error during save:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Load filter preferences from localStorage
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async loadFilterPreferences() {
    try {
      console.log('📂 FilterPersistence: Loading preferences from localStorage');
      
      const rawData = localStorage.getItem(STORAGE_CONFIG.key);
      
      if (!rawData) {
        console.log('📂 FilterPersistence: No saved preferences found, returning defaults');
        const defaults = this.ensureAllBookmakersIncluded({ ...DEFAULT_PREFERENCES });
        return { success: true, data: defaults };
      }

      // Parse JSON data
      let parsedData;
      try {
        parsedData = JSON.parse(rawData);
      } catch (parseError) {
        console.error('❌ FilterPersistence: Failed to parse saved data:', parseError);
        return { 
          success: false, 
          error: 'Corrupted preferences data. Using defaults.',
          data: DEFAULT_PREFERENCES 
        };
      }

      // Validate and migrate data
      const validationResult = this.validatePreferences(parsedData);
      if (!validationResult.isValid) {
        console.warn('⚠️ FilterPersistence: Invalid saved preferences, attempting migration:', validationResult.errors);
        
        const migratedData = this.migratePreferences(parsedData);
        if (migratedData) {
          console.log('✅ FilterPersistence: Successfully migrated preferences');
          const normalized = this.ensureAllBookmakersIncluded(migratedData);
          return { success: true, data: normalized };
        } else {
          console.warn('⚠️ FilterPersistence: Migration failed, using defaults');
          const defaults = this.ensureAllBookmakersIncluded({ ...DEFAULT_PREFERENCES });
          return { success: true, data: defaults };
        }
      }

      console.log('✅ FilterPersistence: Successfully loaded preferences');
      const normalized = this.ensureAllBookmakersIncluded(parsedData);
      return { success: true, data: normalized };

    } catch (error) {
      console.error('❌ FilterPersistence: Unexpected error during load:', error);
      return { 
        success: false, 
        error: error.message,
        data: this.ensureAllBookmakersIncluded({ ...DEFAULT_PREFERENCES }) 
      };
    }
  }

  /**
   * Clear saved filter preferences
   * @returns {Promise<{success: boolean, error?: string}>}
   */
  static async clearFilterPreferences() {
    try {
      console.log('🗑️ FilterPersistence: Clearing saved preferences');
      localStorage.removeItem(STORAGE_CONFIG.key);
      console.log('✅ FilterPersistence: Successfully cleared preferences');
      return { success: true };
    } catch (error) {
      console.error('❌ FilterPersistence: Error clearing preferences:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Export filter preferences as JSON string
   * @param {Object} preferences - Preferences to export
   * @returns {Promise<{success: boolean, data?: string, error?: string}>}
   */
  static async exportFilterPreferences(preferences) {
    try {
      console.log('📤 FilterPersistence: Exporting preferences');
      
      const validationResult = this.validatePreferences(preferences);
      if (!validationResult.isValid) {
        return { success: false, error: validationResult.errors.join(', ') };
      }

      const exportData = {
        ...preferences,
        exportedAt: new Date().toISOString(),
        exportedFrom: 'ArbiWin Filter System',
        version: STORAGE_CONFIG.version,
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      console.log('✅ FilterPersistence: Successfully exported preferences');
      return { success: true, data: jsonString };

    } catch (error) {
      console.error('❌ FilterPersistence: Error exporting preferences:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Import filter preferences from JSON string
   * @param {string} jsonString - JSON string to import
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  static async importFilterPreferences(jsonString) {
    try {
      console.log('📥 FilterPersistence: Importing preferences');
      
      let parsedData;
      try {
        parsedData = JSON.parse(jsonString);
      } catch (parseError) {
        return { success: false, error: 'Invalid JSON format' };
      }

      // Validate imported data
      const validationResult = this.validatePreferences(parsedData);
      if (!validationResult.isValid) {
        return { success: false, error: validationResult.errors.join(', ') };
      }

      // Save imported preferences
      const saveResult = await this.saveFilterPreferences(parsedData);
      if (!saveResult.success) {
        return saveResult;
      }

      console.log('✅ FilterPersistence: Successfully imported preferences');
      return { success: true, data: parsedData };

    } catch (error) {
      console.error('❌ FilterPersistence: Error importing preferences:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Validate filter preferences structure and content
   * @param {Object} preferences - Preferences to validate
   * @returns {{isValid: boolean, errors: string[]}}
   */
  static validatePreferences(preferences) {
    const errors = [];

    if (!preferences || typeof preferences !== 'object') {
      errors.push('Preferences must be an object');
      return { isValid: false, errors };
    }

    // Check selectedBookmakers
    if (!Array.isArray(preferences.selectedBookmakers)) {
      errors.push('selectedBookmakers must be an array');
    } else {
      // Validate each bookmaker ID
      for (const bookmakerId of preferences.selectedBookmakers) {
        if (typeof bookmakerId !== 'string') {
          errors.push('All bookmaker IDs must be strings');
          break;
        }
        if (!VALID_BOOKMAKER_IDS.has(bookmakerId)) {
          errors.push(`Invalid bookmaker ID: ${bookmakerId}`);
        }
      }
    }

    // Check version (optional)
    if (preferences.version && typeof preferences.version !== 'string') {
      errors.push('Version must be a string');
    }

    // Check lastUpdated (optional)
    if (preferences.lastUpdated && typeof preferences.lastUpdated !== 'string') {
      errors.push('lastUpdated must be a string');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Migrate preferences from older schema versions
   * @param {Object} preferences - Preferences to migrate
   * @returns {Object|null} - Migrated preferences or null if migration failed
   */
  static migratePreferences(preferences) {
    try {
      console.log('🔄 FilterPersistence: Migrating preferences from version:', preferences.version || 'unknown');

      // Handle missing schema version (legacy data)
      if (!preferences.schemaVersion) {
        console.log('🔄 FilterPersistence: Migrating legacy preferences');
        
        // Ensure selectedBookmakers is an array
        if (!Array.isArray(preferences.selectedBookmakers)) {
          preferences.selectedBookmakers = Object.keys(BOOKMAKERS);
        }

        // Filter out invalid bookmaker IDs
        preferences.selectedBookmakers = preferences.selectedBookmakers.filter(
          id => VALID_BOOKMAKER_IDS.has(id)
        );

        // If no valid bookmakers, use all
        if (preferences.selectedBookmakers.length === 0) {
          preferences.selectedBookmakers = Object.keys(BOOKMAKERS);
        }

        preferences.schemaVersion = 1;
        preferences.version = STORAGE_CONFIG.version;
      }

      // Future migration logic can be added here for schema version 2, 3, etc.
      // Example:
      // if (preferences.schemaVersion === 1) {
      //   // Migrate from schema 1 to 2
      //   preferences.schemaVersion = 2;
      // }

      console.log('✅ FilterPersistence: Migration completed successfully');
      return preferences;

    } catch (error) {
      console.error('❌ FilterPersistence: Migration failed:', error);
      return null;
    }
  }

  /**
   * Ensure that all currently supported bookmakers are included in the selection.
   * This prevents newly added bookmakers (e.g., Bwin) from being unintentionally excluded
   * for users who saved preferences before the bookmaker was introduced.
   * @param {Object} preferences
   * @returns {Object} normalized preferences
   */
  static ensureAllBookmakersIncluded(preferences) {
    const normalized = {
      ...preferences,
      selectedBookmakers: Array.isArray(preferences.selectedBookmakers)
        ? preferences.selectedBookmakers.filter(id => VALID_BOOKMAKER_IDS.has(id))
        : [],
    };

    const allBookmakerIds = Object.keys(BOOKMAKERS);
    const selectedSet = new Set(normalized.selectedBookmakers);
    const missingBookmakers = allBookmakerIds.filter((id) => !selectedSet.has(id));

    if (missingBookmakers.length > 0) {
      console.log('➕ FilterPersistence: Adding newly supported bookmakers to selection:', missingBookmakers);
      normalized.selectedBookmakers = [...normalized.selectedBookmakers, ...missingBookmakers];
    }

    return normalized;
  }

  /**
   * Get storage usage information
   * @returns {Object} Storage usage stats
   */
  static getStorageInfo() {
    try {
      const data = localStorage.getItem(STORAGE_CONFIG.key);
      const size = data ? new Blob([data]).size : 0;
      
      return {
        hasData: !!data,
        sizeBytes: size,
        sizeKB: Math.round(size / 1024 * 100) / 100,
        key: STORAGE_CONFIG.key,
        version: STORAGE_CONFIG.version,
      };
    } catch (error) {
      console.error('❌ FilterPersistence: Error getting storage info:', error);
      return {
        hasData: false,
        sizeBytes: 0,
        sizeKB: 0,
        key: STORAGE_CONFIG.key,
        version: STORAGE_CONFIG.version,
        error: error.message,
      };
    }
  }

  /**
   * Check if localStorage is available and working
   * @returns {boolean} True if localStorage is available
   */
  static isStorageAvailable() {
    try {
      const testKey = '__arbiwin_storage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      console.warn('⚠️ FilterPersistence: localStorage not available:', error.message);
      return false;
    }
  }
}

export default FilterPersistence;
